import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SecurityService } from 'src/app/services/security/security.service';

@Component({
  selector: 'app-verify-2fa-login',
  templateUrl: './verify-2fa-login.component.html',
  styleUrls: ['./verify-2fa-login.component.css']
})
export class Verify2faLoginComponent implements OnInit {

  @Input() tempToken: string = '';
  @Input() twoFactorMethod: 'sms' | 'authenticator' = 'authenticator';
  @Input() phoneNumber: string | null = null;
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  form: FormGroup;
  submit_disabled = false;
  errors: any[] = [];
  useBackupCode = false;
  trustDevice = false;
  remainingTime = 90;
  timerInterval: any;

  constructor(
    private formBuilder: FormBuilder,
    private securityService: SecurityService
  ) {
    this.form = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$|^[A-Z0-9]{4}-[A-Z0-9]{4}$/)]]
    });
  }

  ngOnInit(): void {
    this.startTimer();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  startTimer(): void {
    this.timerInterval = setInterval(() => {
      this.remainingTime--;
      if (this.remainingTime <= 0) {
        clearInterval(this.timerInterval);
        this.errors = [{ msg: 'El tiempo ha expirado. Por favor, inicia sesión nuevamente.' }];
        this.submit_disabled = true;
      }
    }, 1000);
  }

  get formattedTime(): string {
    const minutes = Math.floor(this.remainingTime / 60);
    const seconds = this.remainingTime % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  toggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    this.errors = [];
    
    if (this.useBackupCode) {
      this.form.get('code')?.setValidators([
        Validators.required,
        Validators.pattern(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/)
      ]);
    } else {
      this.form.get('code')?.setValidators([
        Validators.required,
        Validators.pattern(/^\d{6}$/)
      ]);
    }
    this.form.get('code')?.updateValueAndValidity();
    this.form.get('code')?.setValue('');
  }

  async submit(): Promise<void> {
    this.errors = [];
    
    // Si es SMS, usar endpoint diferente
    if (this.twoFactorMethod === 'sms') {
      await this.submitSMS();
      return;
    }
    
    // Lógica original para Authenticator
    
    if (this.form.valid && !this.submit_disabled) {
      this.submit_disabled = true;
      const code = this.form.get('code')?.value;

      try {
        const resp = await this.securityService.verifyLogin2FA(
          this.tempToken,
          code,
          this.useBackupCode,
          this.trustDevice
        );

        if (resp.ok) {
          this.onSuccess.emit(resp);
        }
      } catch (error: any) {
        if (error?.error?.msg) {
          this.errors = [{ msg: error.error.msg }];
        } else {
          this.errors = [{ msg: 'Error al verificar el código. Intenta nuevamente.' }];
        }
        this.submit_disabled = false;
      }
    }
  }

  async submitSMS(): Promise<void> {
    if (this.form.valid && !this.submit_disabled) {
      this.submit_disabled = true;
      const code = this.form.get('code')?.value;

      try {
        const resp = await this.securityService.verifyLoginSMS2FA(
          this.tempToken,
          code,
          this.trustDevice
        );

        if (resp.ok) {
          this.onSuccess.emit(resp);
        }
      } catch (error: any) {
        if (error?.error?.msg) {
          this.errors = [{ msg: error.error.msg }];
        } else {
          this.errors = [{ msg: 'Código SMS inválido o expirado. Intenta nuevamente.' }];
        }
        this.submit_disabled = false;
      }
    }
  }

  async resendSMS(): Promise<void> {
    if (this.submit_disabled) return;
    
    this.submit_disabled = true;
    this.errors = [];
    
    try {
      await this.securityService.requestSMSCode(this.tempToken);
      this.errors = [{ msg: 'Código reenviado exitosamente' }];
      setTimeout(() => {
        this.errors = [];
      }, 3000);
    } catch (error: any) {
      this.errors = [{ msg: error?.error?.msg || 'Error al reenviar código' }];
    } finally {
      this.submit_disabled = false;
    }
  }

  cancel(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.onCancel.emit();
  }

  formatInput(event: any): void {
    if (this.useBackupCode) {
      let value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (value.length > 4) {
        value = value.slice(0, 4) + '-' + value.slice(4, 8);
      }
      this.form.get('code')?.setValue(value, { emitEvent: false });
    }
  }
}
